import type { Building, Scenario } from "../types";

type CapacityBuilding = Pick<Building, "enrolmentCapacity"> &
  Partial<Pick<Building, "comesOnlineYear">>;

export const CAPACITY_LABELS = {
  planningTarget: "Planning enrolment target",
  scheduledClassroomSeats: "Scheduled classroom seats",
  enrolmentCapacityBasis: "Enrolment capacity basis",
} as const;

function positiveFinite(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : null;
}

/** Legacy imports used `1` as a display sentinel for spaces whose capacity was
 * unavailable. A one-seat school building is not a credible scheduled teaching
 * capacity, so keep those values unavailable in Board artifacts rather than
 * silently adding them to the subtotal. */
export function scheduledClassroomSeatValue(
  value: number | null | undefined,
): number | null {
  const seats = positiveFinite(value);
  return seats !== null && seats > 1 ? seats : null;
}

export function scheduledClassroomSeatsForYear(
  buildings: readonly CapacityBuilding[],
  year = Number.POSITIVE_INFINITY,
): number {
  return buildings.reduce((total, building) => {
    const seats = scheduledClassroomSeatValue(building.enrolmentCapacity) ?? 0;
    const onlineYear =
      typeof building.comesOnlineYear === "number" &&
      Number.isFinite(building.comesOnlineYear)
        ? building.comesOnlineYear
        : 1;
    return onlineYear <= year ? total + seats : total;
  }, 0);
}

export function resolveEnrolmentCapacityBasis(
  targetEnrolment: number | null | undefined,
  scheduledSeats: number,
): number {
  return positiveFinite(targetEnrolment) ?? Math.max(0, scheduledSeats);
}

export function capacityDisclosureFromInputs(
  targetEnrolment: number | null | undefined,
  buildings: readonly CapacityBuilding[],
) {
  const planningTarget = positiveFinite(targetEnrolment);
  const scheduledClassroomSeats = scheduledClassroomSeatsForYear(buildings);
  const enrolmentCapacityBasis = resolveEnrolmentCapacityBasis(
    planningTarget,
    scheduledClassroomSeats,
  );

  return {
    planningTarget,
    scheduledClassroomSeats,
    enrolmentCapacityBasis,
    hasPlanningOverride: planningTarget !== null,
    labels: CAPACITY_LABELS,
    disclosure: planningTarget
      ? `${CAPACITY_LABELS.planningTarget} ${planningTarget.toLocaleString("en-AU")} overrides ${scheduledClassroomSeats.toLocaleString("en-AU")} ${CAPACITY_LABELS.scheduledClassroomSeats.toLowerCase()} for enrolment modelling.`
      : `${CAPACITY_LABELS.scheduledClassroomSeats} are the enrolment modelling basis because no planning target override is set.`,
  };
}

export function capacityDisclosure(
  scenario: Pick<Scenario, "assumptions" | "buildings">,
) {
  return capacityDisclosureFromInputs(
    scenario.assumptions.targetEnrolment,
    scenario.buildings,
  );
}
